import { useMemo, useState } from 'react';
import { IRecipient } from '@nkr/models/index';
import { BodyScrollEvent, CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowClickedEvent, RowDataChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button, Glyphicon } from 'react-bootstrap';
import { GridWrapper } from './styles';
import SortableHeaderComponent from '../center/list/common/sortable-header';
import { Utility } from '@nkr/services/utility';
import { RowNumberRenderer } from '@nkr/views/microsite/list/renderers';

interface IProps {
    data: IRecipient[],
    centerId: number,
    showPanel: boolean,
    onViewRegistration: (id: number) => void,
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.columnApi.applyColumnState({
            state: [
                {
                    colId: 'followUpDate',
                    sort: 'asc'
                }
            ]
        });

        e.api.sizeColumnsToFit();
    }

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const columns: ColDef[] = [
        {
            field: 'dateCreatedOn',
            headerName: 'R#',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            cellRendererFramework: RowNumberRenderer
        },
        {
            field: 'centerAlias',
            headerName: 'Center',
            width: 200,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRecipient;

                return (
                    <a href={'/center/' + data.centerId}>
                        {data.centerAlias}
                    </a>
                );

            }
        },
        {
            field: 'dateCreatedOn',
            headerName: 'Registered',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registered</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            field: 'dateUpdated',
            headerName: 'Last Update',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last<br />Update</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            field: 'transplantDate',
            headerName: 'Last Update',
            width: 180,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Transplant<br />Date</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.data.transplantDate ? params.data.transplantDate : '--',
            comparator: Utility.dateComparator
        },
        {
            field: 'alias',
            headerName: 'Alias',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
        },
        {
            field: 'firstName',
            headerName: 'First Name',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First Name</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.data.firstName ? params.data.firstName : '--',
        },
        {
            field: 'lastName',
            headerName: 'Last Name',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Name</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.data.lastName ? params.data.lastName : '--',
        },
        {
            field: 'gender',
            headerName: 'Gender',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Gender</SortableHeaderComponent>,
        },
        {
            field: 'age',
            headerName: 'Age',
            width: 70,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Age</SortableHeaderComponent>,
        },
        {
            field: 'bloodType',
            headerName: 'ABO',
            width: 70,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ABO</SortableHeaderComponent>,
        },
        {
            field: 'monthsWaitKpd',
            headerName: 'Wait Time KPD',
            width: 80,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>WT<br />KPD</SortableHeaderComponent>,
        },
        {
            field: 'monthsWaitVoucher',
            headerName: 'Wait Time Voucher',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>WT<br />Voucher</SortableHeaderComponent>,
        },
        {
            field: 'monthsWaitTotal',
            headerName: 'Wait Time Total',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>WT<br />Total</SortableHeaderComponent>,
        },
        {
            field: 'status',
            headerName: 'Status',            
            width: 250,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,  
            comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {                
                let a = nodeA.data.status;
                let b = nodeB.data.status;
               
                if(nodeA.data.isDeleted)
                    a = "Deleted";
                if(nodeB.data.isDeleted)
                    b = "Deleted";
                       
                if (a == b) return 0;
                return (a > b) ? 1 : -1;
            },              
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipient;

                if (data.isDeleted) {
                    return <>Deleted</>
                }

                return <>{params.value}</>
            },
        },
        {
            colId: 'isExportable',
            field: 'isExportable',
            headerName: 'Exportable',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Exportable</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipient;

                if (data.isExportable == true) {
                    return <Glyphicon glyph='ok' />;
                }
                else
                    return <>--</>;

            }
        },
        {
            field: 'actionId',
            headerName: '',
            width: 150,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRecipient;
                return (
                    <Button bsStyle='primary' bsSize='xs' onClick={() => props.onViewRegistration(data.id)}>Edit Record</Button>
                )
            }
        }
    ]

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={38}
                suppressCellSelection={true}
                enableCellTextSelection={true}
                suppressContextMenu={true}
                rowStyle={{ overflow: 'visible' }}
                noRowsOverlayComponentFramework={() => <div>...</div>}
                onGridReady={onGridReady}
                onGridSizeChanged={onGridSizeChanged}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
                onBodyScroll={onBodyScroll}
                onCellClicked={(e: CellClickedEvent) => {
                    if (e.colDef.field != 'ctrAlias' && e.colDef.field != 'actionId' && e.colDef.field != 'alias') {
                        let { id } = (e.data as IRecipient);
                        props.onViewRegistration(id);
                        e.node.setSelected(props.showPanel);
                    }
                }}
                getRowStyle={(params: any) => {
                    var retcolor = {};

                    if (params.data.recpIsDeleted) {
                        if (params.node.rowIndex % 2 == 0) {
                            retcolor = { background: '#ffcccc' };
                        } else {
                            retcolor = { background: '#ffdddd' };
                        }
                    }

                    if (params.node.isSelected()) {
                        return { background: '#d7e6f4' };
                    }

                    return retcolor;
                }}
            />
        </GridWrapper>
    );
}

export default Grid;